<script>
    import app from '../data/app.svelte.js';
    import persistent from '../data/persistent.svelte';
    import { currentSession, currentSessionId } from '../data/session.svelte.js';

    import { _ } from 'svelte-i18n';
    import { next, previous, goto, replace } from '../helpers/navigation.svelte.js';
    import { fetchApi } from '../helpers/api.svelte.js';

    import Section from '../components/Section.svelte';
    import Navigation from '../components/Navigation.svelte';
    import Button from '../components/Button.svelte';
    import Back from '../components/Back.svelte';
    import Loader from '../components/Loader.svelte';

    import Datepicker from '../components/Schedule/Datepicker.svelte';
    import Timepicker from '../components/Schedule/Timepicker.svelte';
    import Waitlist from '../components/Schedule/Waitlist.svelte';

    import Icon from '../components/Icon.svelte';
    import arrowRightIcon from '../assets/icons/arrow-right.svg?raw';

    import { DateTime } from 'luxon';


    const current = 'schedule';

    function goBack() {
        currentSession.date = null;
        currentSession.time = null;

        replace(previous(current), 'left');
    }

    function goNext() {
        goto(next(current), 'right');
    }



    let availableDates = $state([]);
    let availableTimes = $state([]);
    let busyTimeframes = $state({});
    let waitlistTimeframes = $state([]);

    let datesLoading = $state(false);
    let timesLoading = $state(false);
    let timepickerRef = $state();
    let nextButtonRef = $state();



    /* Calendar view

       Every month the calendar is flipped to reports a view change, and a
       held key or a fast thumb produces several a second. Only the month the
       customer ends up looking at is worth a request, so view changes are
       coalesced: at most one dates request is in flight, and while it runs
       only the most recent view is kept. Months skimmed past are never
       requested. The datepicker itself stops a year out. */

    let ranges = [];        // months already fetched, by the ISO date they start at
    let wanted = null;      // the latest view still to fetch, or null
    let loading = false;    // a dates request is in flight

    function onViewChanged(view) {
        currentSession.date = null;
        currentSession.time = null;
        availableTimes = [];

        wanted = view;

        if (!loading) {
            pump();
        }
    }

    async function pump() {
        while (wanted) {
            const view = wanted;
            wanted = null;

            if (ranges.includes(view.month)) {
                continue;
            }

            loading = true;

            try {
                await getRange(view.month, view.start, view.limit);
            }
            finally {
                loading = false;
            }
        }
    }

    async function getCurrentMonth() {
        const now = DateTime.now();
        const endOfMonth = now.endOf('month');

        let limit = Math.ceil(endOfMonth.diff(now, 'days').days);

        wanted = { month: now.toISODate(), start: 0, limit };

        await pump();

        // Auto-select the first available date so times appear immediately
        if (availableDates.length > 0 && !currentSession.date) {
            currentSession.date = availableDates[0];
            getTimes();
        }
    }

    async function getRange(month, start, limit) {
        datesLoading = true;

        try {
            const form = new FormData();
            form.append('session', currentSessionId);
            form.append('client', currentSession.client);
            form.append('salon', currentSession.salon);
            form.append('treatment', currentSession.treatment?.id);
            form.append('employee', currentSession.employee?.id);
            form.append('start', start);
            form.append('limit', limit);

            const data = await fetchApi({ 
                endpoint: 'OnlineAppointment.Remote.Dates/get', 
                data: form 
            });

            // Detect misconfiguration: minimum date is after maximum date
            if (data.minimum && data.maximum && data.minimum > data.maximum) {
                replace('error/salon-configuration-error', 'right');
                return;
            }

            ranges.push(month);

            data.dates.forEach(d => {
                availableDates.push(d.date);
            })

            // Store busy timeframes for waitlist dates
            // Prefer busy (new format) over waitlist (legacy)
            if (data.busy && Array.isArray(data.busy)) {
                data.busy.forEach(entry => {
                    busyTimeframes[entry.date] = entry.timeframe;
                });
            } else if (data.waitlist && Array.isArray(data.waitlist)) {
                data.waitlist.forEach(date => {
                    busyTimeframes[date] = ['morning', 'afternoon'];
                });
            }
        }
        finally {
            datesLoading = false;
        }
    }



    /* Times */
    
    function onDateChange(date) {
        if (date !== currentSession.date) {
            currentSession.time = null;
            availableTimes = [];
        }

        currentSession.date = date;
        
        if (availableDates.includes(date) === false) {
            return;
        }

        getTimes();
    }



    async function getTimes() {
        timesLoading = true;

        const form = new FormData();
        form.append('session', currentSessionId);
        form.append('client', currentSession.client);
        form.append('salon', currentSession.salon);
        form.append('treatment', currentSession.treatment?.id);
        form.append('employee', currentSession.employee?.id);
        form.append('date', currentSession.date);

        const data = await fetchApi({ 
            endpoint: 'OnlineAppointment.Remote.Times/get', 
            data: form 
        });

        availableTimes = [];

        data.times.forEach(t => {
            availableTimes.push(t.time);
        })

        timesLoading = false;
    }


    function onTimeChange(time) {
        currentSession.time = time;
        currentSession.type = 'appointment';
        waitlistTimeframes = []; // Clear waitlist selection when time is selected
    }


    const initialLoad = getCurrentMonth();

    function onWaitlistChange(timeframes) {
        waitlistTimeframes = timeframes;
        // Store waitlist timeframes in currentSession.time as comma-separated string
        currentSession.time = timeframes.length > 0 ? timeframes.join(',') : null;
        currentSession.type = timeframes.length > 0 ? 'waitlist' : 'appointment';
    }

</script>

<svelte:head>
    <title>{$_('appointment_title')} - {$_('schedule')}</title>
</svelte:head>

<Section title={$_('schedule')}>
    {#snippet back()}
        <Back onclick={goBack} />
    {/snippet}
    
    {#await initialLoad}
        <Loader delay={1000} />
    {:then}
        <div>
            <Datepicker value={currentSession.date} {availableDates} {busyTimeframes} {onDateChange} {onViewChanged} skipLabel={$_('skip_to_time')} onSkip={() => timepickerRef?.focus()} />

            {#if datesLoading || timesLoading}
                <Loader delay={1000} />
            {:else if currentSession.date}
                {#if availableTimes.length}
                    <Timepicker bind:this={timepickerRef} value={currentSession.time} {availableTimes} {onTimeChange} onConfirm={() => nextButtonRef?.focus()} />
                {:else if app.settings.waitlist?.enabled && busyTimeframes[currentSession.date]}
                    <Waitlist value={waitlistTimeframes} date={currentSession.date} availableTimeframes={busyTimeframes[currentSession.date]} onChange={onWaitlistChange} />
                {:else}
                    <p>{$_('no_times_available')}</p>
                {/if}
            {:else}
                <p>{$_('select_day_first')}</p>
            {/if}
        </div>
    {/await}

    {#snippet navigation()}
        <Navigation>
            <Button bind:this={nextButtonRef} color="accent" onclick={goNext} disabled={!currentSession.date || (!currentSession.time && waitlistTimeframes.length === 0)}>
                {$_('next')}
                <Icon data={arrowRightIcon} />
            </Button>
        </Navigation>
    {/snippet}
</Section>

<style>

    div {
        display: flex;
        flex-wrap: wrap;
        gap: 2em;
        margin-top: 1em;
    }

</style>